import { createSelector } from 'reselect';
import lodash from 'lodash';
import { reportTypes } from '../utils/constants';

const defaultDataSource = {
    values: [],
    loading: false,
};

export const getSelectedReport = state => lodash.get(state, 'institutionReport.selectedReport', null);
export const getDataSources = state => lodash.get(state, 'institutionReport.dataSources', {});
export const getSelectedChoices = state => lodash.get(state, 'institutionReport.selectedChoices', {});
export const getReportData = state => lodash.get(state, 'institutionReport.reportData', {});

export const getParametersByChoice = createSelector([getSelectedReport], selectedReport => {
    if (!selectedReport) {
        return;
    }
    const { parameters } = selectedReport;
    return lodash.groupBy(parameters, parameter => parameter.choice);
});

export const getReportOptions = createSelector(
    [getParametersByChoice, getSelectedChoices, getDataSources, getReportData],
    (parametersByChoice, selectedChoices, dataSources, reportData) => {
        return lodash.map(parametersByChoice, (choiceParameters, choice) => {
            const isChoiceRequired = lodash.some(choiceParameters, ['isChoiceRequired', true]);
            const isChoiceHidden = lodash.some(choiceParameters, ['isChoiceHidden', true]);
            const choiceLabel =
                lodash.reduce(choiceParameters, (label, parameter) => parameter.choiceLabel || label, '') ||
                'Choose an option';
            const choices = lodash.map(choiceParameters, parameter => {
                const { id, label, type, source, target, targetKey, submit, dataKey } = parameter;
                const dataSource = lodash.get(dataSources, source, defaultDataSource);
                return {
                    id,
                    choice,
                    label,
                    type,
                    source,
                    target,
                    targetKey,
                    submit,
                    dataKey,
                    isSelected: selectedChoices[choice] === id,
                    loading: Boolean(dataSource.loading),
                    values: lodash.get(dataSource, 'values', []),
                    selected: lodash.get(reportData, dataKey, null),
                };
            });
            return {
                choice,
                choices,
                label: choiceLabel,
                required: isChoiceRequired,
                hidden: isChoiceHidden,
                selectedChoice: selectedChoices[choice],
            };
        });
    },
);

export const getSubmitData = createSelector([getSelectedReport, getReportData], (selectedReport, reportData) => {
    const submitParams = lodash.filter(selectedReport.parameters, 'submit');
    const submitData = lodash.reduce(
        submitParams,
        (submitData, submitParam) => {
            const { dataKey, target, targetKey } = submitParam;
            submitData[target] = extractReportData(reportData, dataKey, targetKey);
            return submitData;
        },
        {},
    );
    submitData['reportType'] = submitData['reportType'] || selectedReport.reportType;
    return submitData;
});

function extractReportData(reportData, dataKey, targetKey) {
    const value = lodash.get(reportData, dataKey, null);
    if (lodash.isArray(value)) {
        return targetKey ? lodash.map(value, targetKey) : value;
    }
    return targetKey ? lodash.get(value, targetKey, null) : value;
}

export const getCanSubmitReport = createSelector([getReportOptions, getSubmitData], (reportOptions, submitData) => {
    if (lodash.isNil(submitData.reportType)) {
        return false;
    }
    const requiredOptions = lodash.filter(reportOptions, { required: true });
    if (submitData.reportType === reportTypes.document) {
        return canDownloadDocumentReport(requiredOptions, submitData);
    }

    return lodash.every(requiredOptions, reportOption => {
        let choice = lodash.find(reportOption.choices, ['id', reportOption.selectedChoice]);
        if (!choice && reportOption.choices.length === 1) {
            choice = lodash.head(reportOption.choices);
        }
        if (!choice) {
            return;
        }
        return (
            lodash.isNil(choice.target) || !lodash.isEmpty(submitData[choice.target])
        );
    });
});

const canDownloadDocumentReport = (requiredOptions, submitData) => {
    return requiredOptions.some(item => {
        let choice = lodash.find(item.choices, ['id', item.selectedChoice]);
        if (!choice) {
            return;
        }
        return lodash.isNil(choice.target) || !lodash.isEmpty(submitData[choice.target]);
    });
};
